/**
 * Full-screen chart modal.
 *
 * Portal-based overlay that re-renders the selected chart at larger dimensions.
 * Closes on Escape key press or backdrop click. Includes download buttons.
 */

import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import type { ChartSpec } from "../../types/index.js";
import BarChartView from "./BarChartView.js";
import PieChartView from "./PieChartView.js";
import LineChartView from "./LineChartView.js";
import GanttChartView from "./GanttChartView.js";
import WordCloudView from "./WordCloudView.js";
import TableView from "./TableView.js";
import type { BarChartConfig, PieChartConfig, LineChartConfig, GanttChartConfig, WordCloudConfig, TableChartConfig, WorkflowChartConfig, KanbanChartConfig } from "../../types/index.js";
import { exportSvgAsPng, exportSvgAsSvg, findSvgInContainer, copyTableAsText, copyKanbanToClipboard } from "./chartUtils.js";
import WorkflowChartView from "./WorkflowChartView.js";
import KanbanBoardView from "./KanbanBoardView.js";

interface ChartModalProps {
	spec: ChartSpec;
	onClose: () => void;
}

export default function ChartModal({ spec, onClose }: ChartModalProps) {
	const modalRef = useRef<HTMLDivElement>(null);
	const chartContainerRef = useRef<HTMLDivElement>(null);
	const [copied, setCopied] = useState(false);

	// Tracks whether a mousedown originated inside the resizer. When the user
	// drags the native CSS resize handle and releases outside (on the backdrop
	// padding), the resulting click event has target === currentTarget and would
	// normally close the modal. This flag suppresses that spurious close.
	const resizeDragActiveRef = useRef(false);

	const isTable = spec.type === "table";
	const isKanban = spec.type === "kanban";
	const isCopyable = isTable || isKanban;
	const copyLabel = isKanban ? "Copy board to clipboard" : "Copy table to clipboard";

	const handleCopy = async () => {
		let success: boolean;
		if (isKanban) {
			success = await copyKanbanToClipboard(spec.config as KanbanChartConfig);
		} else {
			success = await copyTableAsText(spec.config as TableChartConfig);
		}
		if (success) {
			setCopied(true);
			setTimeout(() => setCopied(false), 2000);
		}
	};

	// Close on Escape key
	useEffect(() => {
		const handleKeyDown = (e: KeyboardEvent) => {
			if (e.key === "Escape") onClose();
		};
		document.addEventListener("keydown", handleKeyDown);
		return () => document.removeEventListener("keydown", handleKeyDown);
	}, [onClose]);

	// Trap focus within modal
	useEffect(() => {
		const firstFocusable = modalRef.current?.querySelector<HTMLElement>(
			"button, [href], input, select, textarea, [tabindex]:not([tabindex='-1'])"
		);
		firstFocusable?.focus();
	}, []);

	// Prevent body scroll while open
	useEffect(() => {
		const prev = document.body.style.overflow;
		document.body.style.overflow = "hidden";
		return () => {
			document.body.style.overflow = prev;
		};
	}, []);

	const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
		// Suppress close if the mousedown started inside the resizer (i.e. the user
		// was dragging the resize handle and released outside the resizer bounds).
		if (resizeDragActiveRef.current) {
			resizeDragActiveRef.current = false;
			return;
		}
		if (e.target === e.currentTarget) onClose();
	};

	// Set the drag flag when any mousedown occurs on the resizer or its children.
	const handleResizerMouseDown = () => {
		resizeDragActiveRef.current = true;
	};

	const handleDownloadPng = async () => {
		if (!chartContainerRef.current) return;
		const svgEl = findSvgInContainer(chartContainerRef.current);
		if (svgEl) {
			await exportSvgAsPng(svgEl, `${spec.title.replace(/\s+/g, "-").toLowerCase()}.png`);
		}
	};

	const handleDownloadSvg = () => {
		if (!chartContainerRef.current) return;
		const svgEl = findSvgInContainer(chartContainerRef.current);
		if (svgEl) {
			exportSvgAsSvg(svgEl, `${spec.title.replace(/\s+/g, "-").toLowerCase()}.svg`);
		}
	};

	const renderChart = () => {
		switch (spec.type) {
			case "bar":
				return <BarChartView config={spec.config as BarChartConfig} isModal />;
			case "pie":
				return <PieChartView config={spec.config as PieChartConfig} isModal />;
			case "line":
				return <LineChartView config={spec.config as LineChartConfig} isModal />;
			case "gantt":
				return <GanttChartView config={spec.config as GanttChartConfig} isModal />;
			case "wordcloud":
				return <WordCloudView config={spec.config as WordCloudConfig} isModal />;
			case "table":
				return <TableView config={spec.config as TableChartConfig} isModal />;
			case "workflow":
				return <WorkflowChartView config={spec.config as WorkflowChartConfig} isModal />;
			case "kanban":
				return <KanbanBoardView config={spec.config as KanbanChartConfig} fullscreen isModal />;
			default:
				return <div className="chart-empty-state">Unknown chart type</div>;
		}
	};

	const modalContent = (
		<div
			className="chart-modal-backdrop"
			onClick={handleBackdropClick}
		>
			{/* Resizer wrapper: CSS resize:both does not work on flex children.
			    This non-flex wrapper is resizable; the modal fills it 100%. */}
			<div className="chart-modal-resizer" onMouseDown={handleResizerMouseDown}>
				<div
					className="chart-modal"
					ref={modalRef}
					role="dialog"
					aria-modal="true"
					aria-label={spec.title}
				>
				{/* Modal header */}
				<div className="chart-modal-header">
					<div className="chart-modal-title-group">
						<h2 className="chart-modal-title">{spec.title}</h2>
						{spec.description && (
							<p className="chart-modal-description">{spec.description}</p>
						)}
					</div>

					<div className="chart-modal-actions">
						{/* Table/Kanban: copy to clipboard; other charts: PNG/SVG download buttons */}
						{isCopyable ? (
							<button
								className={`chart-action-btn chart-copy-btn${copied ? " chart-copy-btn--copied" : ""}`}
								onClick={handleCopy}
								aria-label={copied ? "Copied to clipboard" : copyLabel}
								title={copied ? "Copied!" : "Copy to clipboard"}
							>
								{copied ? (
									<>
										<svg viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg" width="12" height="12" aria-hidden="true">
											<path d="M2 7l3.5 3.5L12 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
										</svg>
										Copied!
									</>
								) : (
									<>
										<svg viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg" width="12" height="12" aria-hidden="true">
											<rect x="5" y="1" width="8" height="9" rx="1.5" stroke="currentColor" strokeWidth="1.3" />
											<path d="M9 10v2a1 1 0 01-1 1H2a1 1 0 01-1-1V5a1 1 0 011-1h2" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
										</svg>
										Copy to clipboard
									</>
								)}
							</button>
						) : (
							<div className="chart-download-dropdown-wrapper">
								<button
									className="chart-action-btn"
									aria-label="Download chart"
									onClick={handleDownloadPng}
									title="Download as PNG"
								>
									<svg viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg" width="13" height="13" aria-hidden="true">
										<path d="M7 1v8M4 6.5L7 10l3-3.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
										<path d="M2 12h10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
									</svg>
									PNG
								</button>
								<button
									className="chart-action-btn"
									aria-label="Download chart as SVG"
									onClick={handleDownloadSvg}
									title="Download as SVG"
								>
									SVG
								</button>
							</div>
						)}

						{/* Close button */}
						<button
							className="chart-modal-close"
							onClick={onClose}
							aria-label="Close chart"
						>
							<svg viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg" width="14" height="14" aria-hidden="true">
								<path d="M1 1l12 12M13 1L1 13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
							</svg>
						</button>
					</div>
				</div>

				{/* Chart body */}
				<div className="chart-modal-body" ref={chartContainerRef}>
					{renderChart()}
				</div>
				</div>
			</div>{/* end chart-modal-resizer */}
		</div>
	);

	return createPortal(modalContent, document.body);
}
